import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { Flex } from '@mantine/core';
import withStoreProvider from '../../../../util/withStoreProvider';
import {
  useVoucherStateStore,
  VoucherStateStore,
  VoucherStateStoreProvider,
} from '../questionnaires/stores/VoucherStateStore';
import { ErrorCode, VoucherAllowanceStatus } from '@web/common';
import { VoucherType } from '@prisma/client';
import { useTranslation } from 'react-i18next';
import {
  showErrorNotification,
  showSuccessNotification,
} from '../../../../component/notification';
import { useWindowSize } from '../../../../hooks';
import { WaitingVoucherRow } from './WaitingVoucherRow';
import { voucherAmountLabel } from '../questionnaires/components/constants';
import { useWaitingStateStore } from '../../../../store';
import { DateTime } from 'luxon';

type RedeemableStatus = {
  type: VoucherAllowanceStatus;
  voucherAllowanceId?: number;
  vouchers?: Array<{ code: string; type: VoucherType }>;
};

const getVoucherProps = (status: RedeemableStatus) => ({
  voucherAllowanceId:
    status.type === VoucherAllowanceStatus.AvailableToBeRedeemed
      ? status.voucherAllowanceId
      : undefined,
  vouchers:
    status.type === VoucherAllowanceStatus.Redeemed
      ? status.vouchers
      : undefined,
});

const WaitingScreenVouchersContent = observer(() => {
  const store = useVoucherStateStore();
  const waitingStateStore = useWaitingStateStore();
  const { t } = useTranslation('questionnaire');
  const isSmallScreen = useWindowSize(600);

  useEffect(() => {
    store.loadVoucherStates();
    return () => store.resetStore();
  }, [store]);

  const surveysWithVouchers = (store.voucherStates ?? []).filter(
    (item) =>
      item.voucherStatus.type ===
        VoucherAllowanceStatus.AvailableToBeRedeemed ||
      item.voucherStatus.type === VoucherAllowanceStatus.Redeemed,
  );

  const bms = store.bmsSampleVoucherStatus;
  // served by the bdm-state endpoint (not the voucher-states response), so the
  // status lives in the WaitingStateStore and must be refreshed after a redeem
  const bdm1 = waitingStateStore.bdmState?.bdm1VoucherStatus ?? null;
  const bdm2 = waitingStateStore.bdmState?.bdm2VoucherStatus ?? null;

  if (surveysWithVouchers.length === 0 && !bms && !bdm1 && !bdm2) return null;

  const formatDate = (date: Date) =>
    DateTime.fromJSDate(date).toFormat('dd.MM.yyyy');

  const handleRedeem = (allowanceId: number, onRedeemed?: () => void) => {
    store.redeemVoucher(
      allowanceId,
      () => {
        onRedeemed?.();
        showSuccessNotification(t('RedeemVoucherSuccess'));
      },
      () => showErrorNotification(ErrorCode.CannotRedeemVoucher),
    );
  };

  // both BDM statuses come from the bdm-state endpoint, so that one needs the refresh
  const redeemBdmVoucher = (allowanceId: number) =>
    handleRedeem(allowanceId, () => waitingStateStore.loadBdmState());

  return (
    <Flex direction="column" maw="1000px" w="100%">
      {bms && (
        <WaitingVoucherRow
          label={t('BmsSample.Title')}
          isFirstRow
          dateLabel={
            bms.broughtAt
              ? t('WaitingVoucher.BroughtAt', {
                  date: formatDate(bms.broughtAt),
                })
              : undefined
          }
          {...getVoucherProps(bms)}
          onRedeem={handleRedeem}
          isSmallScreen={isSmallScreen}
        />
      )}

      {bdm1 && (
        <WaitingVoucherRow
          label={t('Bdm1Voucher.Title')}
          isFirstRow={!bms}
          {...getVoucherProps(bdm1)}
          onRedeem={redeemBdmVoucher}
          isSmallScreen={isSmallScreen}
        />
      )}

      {bdm2 && (
        <WaitingVoucherRow
          label={t('Bdm2Voucher.Title')}
          isFirstRow={!bms && !bdm1}
          {...getVoucherProps(bdm2)}
          redeemAmount={voucherAmountLabel(bdm2.galaxusValue)}
          onRedeem={redeemBdmVoucher}
          isSmallScreen={isSmallScreen}
        />
      )}

      {surveysWithVouchers.map((item, index) => (
        <WaitingVoucherRow
          key={item.surveyLinkId}
          label={`${t('Questionnaire')} ${item.surveyIdentifier}`}
          isFirstRow={!bms && !bdm1 && !bdm2 && index === 0}
          dateLabel={
            item.recordedFilloutDate
              ? t('WaitingVoucher.FilledOutAt', {
                  date: formatDate(item.recordedFilloutDate),
                })
              : undefined
          }
          {...getVoucherProps(item.voucherStatus)}
          onRedeem={handleRedeem}
          isSmallScreen={isSmallScreen}
        />
      ))}
    </Flex>
  );
});

export const WaitingScreenVouchers = withStoreProvider(
  new VoucherStateStore(),
  VoucherStateStoreProvider,
)(WaitingScreenVouchersContent);
